import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  WhatsappLogo,
  ArrowLeft,
  CalendarBlank,
  Users,
  Phone,
  Envelope,
  WarningCircle,
  PawPrint,
  Clock,
  ChatCircle,
  Info,
  CheckCircle,
  XCircle,
} from "@phosphor-icons/react";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { addDays, differenceInDays, format, isBefore, startOfDay } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRICE_PER_NIGHT = 1500;
const MAX_GUESTS = 7;
const MIN_ADVANCE_DAYS = 3;
const WHATSAPP_NUMBER = "528333039700";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function ReservationPage() {
  const [availability, setAvailability] = useState([]);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    guests: "1",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unavailableError, setUnavailableError] = useState(null);

  // Fetch availability
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const response = await axios.get(`${API}/calendar/availability`);
        setAvailability(response.data);
      } catch (error) {
        console.error("Error fetching availability:", error);
      }
    };
    fetchAvailability();
    
    // Refresh every 60 seconds
    const interval = setInterval(fetchAvailability, 60000);
    return () => clearInterval(interval);
  }, []);

  // Calculate nights and total
  const nights = dateRange.from && dateRange.to ? differenceInDays(dateRange.to, dateRange.from) : 0;
  const total = nights * PRICE_PER_NIGHT;

  // Minimum selectable date (3 days from now)
  const today = startOfDay(new Date());
  const minDate = addDays(today, MIN_ADVANCE_DAYS);

  // Get date status
  const getDateStatus = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const entry = availability.find(a => a.date === dateStr);
    return entry?.type || "available";
  };

  // Check if a date is disabled
  const isDateDisabled = (date) => {
    const dateOnly = startOfDay(date);
    // Block dates before minimum
    if (isBefore(dateOnly, minDate)) return true;
    // Check availability
    const status = getDateStatus(date);
    return status !== "available";
  };

  // Check availability when dates change
  useEffect(() => {
    const checkDates = async () => {
      if (!dateRange.from || !dateRange.to) {
        setUnavailableError(null);
        return;
      }

      try {
        const response = await axios.post(`${API}/calendar/check-availability`, {
          start_date: format(dateRange.from, "yyyy-MM-dd"),
          end_date: format(dateRange.to, "yyyy-MM-dd"),
        });

        if (!response.data.available) {
          setUnavailableError(response.data.unavailable_dates);
        } else {
          setUnavailableError(null);
        }
      } catch (error) {
        console.error("Error checking availability:", error);
      }
    };

    checkDates();
  }, [dateRange]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!dateRange.from || !dateRange.to) {
      toast.error("Por favor selecciona las fechas de tu estancia");
      return;
    }

    if (unavailableError) {
      toast.error("Las fechas seleccionadas no están disponibles");
      return;
    }

    if (!formData.name.trim()) {
      toast.error("Por favor ingresa tu nombre");
      return;
    }

    if (!formData.phone.trim()) {
      toast.error("Por favor ingresa tu teléfono");
      return;
    }

    if (parseInt(formData.guests) > MAX_GUESTS) {
      toast.error(`El máximo de personas es ${MAX_GUESTS}`);
      return;
    }

    setIsSubmitting(true);

    try {
      // Create reservation in backend
      await axios.post(`${API}/reservations`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        check_in: format(dateRange.from, "yyyy-MM-dd"),
        check_out: format(dateRange.to, "yyyy-MM-dd"),
        guests: parseInt(formData.guests),
        message: formData.message,
        nights: nights,
        total: total,
      });

      toast.success("¡Reservación creada exitosamente!");

      // Format dates for WhatsApp
      const checkIn = format(dateRange.from, "dd/MM/yyyy", { locale: es });
      const checkOut = format(dateRange.to, "dd/MM/yyyy", { locale: es });

      // Create WhatsApp message
      const message = `Hola, acabo de reservar el departamento en Tampico.
Mi nombre es ${formData.name}.
${formData.email ? `Mi email es ${formData.email}.` : ""}
Mi teléfono es ${formData.phone}.
Quiero hospedarme del ${checkIn} al ${checkOut}.
Seremos ${formData.guests} persona(s).
La estancia es de ${nights} noche(s).
El total es de $${total.toLocaleString()} MXN.
${formData.message ? `Notas: ${formData.message}.` : ""}
¡Espero confirmación!`;

      // Open WhatsApp
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank");

    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al crear la reservación";
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Volver al inicio</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8 md:py-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="mb-8"
        >
          <h1 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-stone-900">
            Reserva tu estancia
          </h1>
          <p className="text-stone-600 mt-2">
            Selecciona tus fechas y completa tu información
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Calendar & Info */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="space-y-6"
          >
            {/* Calendar Card */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-lg text-stone-800 mb-4 flex items-center gap-2">
                <CalendarBlank weight="duotone" className="w-5 h-5 text-[#E05A47]" />
                Selecciona tus fechas
              </h2>

              {/* Important Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-amber-800 text-sm mb-2 flex items-center gap-2">
                  <Info weight="fill" className="w-4 h-4" />
                  Información importante:
                </h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Reserva mínima: 1 noche
                  </li>
                  <li className="flex items-center gap-2">
                    <CalendarBlank className="w-4 h-4" />
                    Anticipación mínima: 3 días
                  </li>
                  <li className="flex items-center gap-2">
                    <WarningCircle className="w-4 h-4" />
                    Las fechas en rojo están reservadas
                  </li>
                  <li className="flex items-center gap-2 text-red-600">
                    <PawPrint className="w-4 h-4" />
                    No se aceptan mascotas
                  </li>
                </ul>
              </div>

              {/* Unavailable Error */}
              {unavailableError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                    <XCircle className="w-5 h-5" />
                    Fechas no disponibles
                  </div>
                  <p className="text-sm text-red-600">
                    Las siguientes fechas ya están ocupadas:
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {unavailableError.map((d, i) => (
                      <span key={i} className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                        {format(new Date(d.date), "dd MMM", { locale: es })}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Calendar */}
              <div className="flex justify-center overflow-visible">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={1}
                  locale={es}
                  disabled={isDateDisabled}
                  modifiers={{
                    reserved: (date) => getDateStatus(date) === "reserved",
                    blocked: (date) => getDateStatus(date) === "blocked",
                  }}
                  modifiersStyles={{
                    reserved: { backgroundColor: "#FEE2E2", color: "#DC2626" },
                    blocked: { backgroundColor: "#E5E7EB", color: "#6B7280" },
                  }}
                  className="rounded-xl border-0 p-0"
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4",
                    caption: "flex justify-center pt-1 relative items-center",
                    caption_label: "text-sm font-medium text-stone-900",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center rounded-md border border-stone-200 hover:bg-stone-100",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex",
                    head_cell: "text-stone-500 rounded-md w-9 font-normal text-[0.8rem]",
                    row: "flex w-full mt-2",
                    cell: "h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
                    day: "h-9 w-9 p-0 font-normal rounded-md hover:bg-stone-100 inline-flex items-center justify-center",
                    day_range_end: "day-range-end",
                    day_selected: "bg-[#E05A47] text-white hover:bg-[#C84938] hover:text-white focus:bg-[#E05A47] focus:text-white",
                    day_today: "bg-stone-100 text-stone-900",
                    day_outside: "text-stone-400 opacity-50",
                    day_disabled: "text-stone-400 opacity-50 line-through",
                    day_range_middle: "bg-[#FEE2E2] rounded-none",
                    day_hidden: "invisible",
                  }}
                  data-testid="reservation-calendar"
                />
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-6 text-sm justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
                  <span className="text-stone-600">Disponible</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
                  <span className="text-stone-600">Reservado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-gray-200 border border-gray-300"></div>
                  <span className="text-stone-600">No disponible</span>
                </div>
              </div>
            </div>

            {/* WhatsApp Help */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <div className="flex items-start gap-3">
                <ChatCircle weight="duotone" className="w-6 h-6 text-[#25D366] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-stone-800">¿Tienes dudas?</h3>
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hola, tengo una pregunta sobre el departamento...")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#25D366] hover:underline text-sm font-medium"
                  >
                    Contáctanos por WhatsApp antes de reservar →
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-stone-200 p-6 space-y-5">
              <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-lg text-stone-800 mb-4">
                Información del huésped
              </h2>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-stone-700 flex items-center gap-1">
                  Nombre completo *
                </Label>
                <Input
                  id="name"
                  placeholder="Juan Pérez"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 rounded-xl border-stone-200 focus:border-[#E05A47] focus:ring-[#E05A47]"
                  data-testid="input-name"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-700 flex items-center gap-2">
                  <Envelope className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ejemplo@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-12 rounded-xl border-stone-200 focus:border-[#E05A47] focus:ring-[#E05A47]"
                  data-testid="input-email"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-stone-700 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Teléfono *
                </Label>
                <Input
                  id="phone"
                  placeholder="+52 833 123 4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-12 rounded-xl border-stone-200 focus:border-[#E05A47] focus:ring-[#E05A47]"
                  data-testid="input-phone"
                />
              </div>

              {/* Guests */}
              <div className="space-y-2">
                <Label htmlFor="guests" className="text-stone-700 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Número de huéspedes *
                </Label>
                <Select
                  value={formData.guests}
                  onValueChange={(value) => setFormData({ ...formData, guests: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl border-stone-200" data-testid="select-guests">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {num} {num === 1 ? "persona" : "personas"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">Capacidad máxima: 7 personas</p>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-stone-700">
                  Notas adicionales (opcional)
                </Label>
                <textarea
                  id="message"
                  placeholder="Alguna petición especial o información adicional..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full h-24 px-4 py-3 rounded-xl border border-stone-200 focus:border-[#E05A47] focus:ring-1 focus:ring-[#E05A47] focus:outline-none resize-none text-sm"
                  data-testid="input-message"
                />
              </div>

              {/* Summary */}
              {dateRange.from && dateRange.to && !unavailableError && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                    <CheckCircle className="w-5 h-5" />
                    Fechas disponibles
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600">Check-in</span>
                    <span className="font-medium text-stone-900">{format(dateRange.from, "dd MMM yyyy", { locale: es })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600">Check-out</span>
                    <span className="font-medium text-stone-900">{format(dateRange.to, "dd MMM yyyy", { locale: es })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600">Noches</span>
                    <span className="font-medium text-stone-900">{nights}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600">$1,500 MXN x {nights} noche(s)</span>
                    <span className="font-medium text-stone-900">${total.toLocaleString()} MXN</span>
                  </div>
                  <div className="border-t border-green-200 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-semibold text-stone-800">Total</span>
                      <span className="font-bold text-lg text-[#E05A47]">${total.toLocaleString()} MXN</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || unavailableError || !dateRange.from || !dateRange.to}
                className="w-full h-14 bg-[#E05A47] hover:bg-[#C84938] text-white rounded-xl text-lg font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-reservation-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Procesando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <WhatsappLogo weight="fill" className="w-6 h-6" />
                    Reservar y contactar por WhatsApp
                  </span>
                )}
              </Button>

              <p className="text-center text-xs text-stone-500">
                Al reservar, las fechas quedarán bloqueadas automáticamente
              </p>
            </form>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
